import { useQuery } from '@tanstack/react-query';
import mermaid from 'mermaid';
import { useId } from 'react';
import { theme } from '../../theme';
import { GuardianErrorComponent } from './GuardianMarkdown';
import { ThinkingLoader } from './ThinkingLoader';

/**
 * Normalizes hex color to full 6-digit format (e.g., #f9f -> #ff99ff)
 */
function normalizeHexColor(hex: string): string {
  const cleanHex = hex.replace('#', '').trim();
  if (cleanHex.length === 3) {
    // Expand shorthand hex (#f9f -> #ff99ff)
    return `#${cleanHex
      .split('')
      .map((char) => char + char)
      .join('')}`;
  }
  return hex.startsWith('#') ? hex : `#${cleanHex}`;
}

/**
 * Calculates the relative luminance of a color (0-1 scale)
 * Based on WCAG 2.0 formula: https://www.w3.org/WAI/GL/wiki/Relative_luminance
 */
function getLuminance(hex: string): number {
  const normalizedColor = normalizeHexColor(hex);
  const cleanHex = normalizedColor.replace('#', '');

  // Convert hex to RGB
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  // Convert to linear RGB
  const [rLinear, gLinear, bLinear] = [r, g, b].map((val) => {
    return val <= 0.03928 ? val / 12.92 : ((val + 0.055) / 1.055) ** 2.4;
  });

  // Calculate relative luminance
  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
}

/**
 * Returns an appropriate text color (white or black) based on the background color's luminance
 */
function getContrastTextColor(backgroundColor: string): string {
  const luminance = getLuminance(backgroundColor);
  // Use white text for dark backgrounds (luminance < 0.5), black for light backgrounds
  return luminance < 0.5 ? '#ffffff' : '#000000';
}

/**
 * Preprocesses Mermaid code to add text color styles based on fill colors for readability
 * Modifies style declarations to include color attribute when fill is specified
 */
function enhanceMermaidCodeForReadability(code: string): string {
  // Match style declarations: style NODE_ID fill:#color,stroke:#color,stroke-width:2px
  // This regex captures the entire style line
  const styleRegex = /style\s+(\w+)\s+([^\n]+)/g;

  return code.replace(styleRegex, (match, nodeId, styleProperties) => {
    // Check if color is already specified
    if (styleProperties.includes('color:')) {
      // Color already set, don't modify
      return match;
    }

    // Extract fill color from style properties
    const fillMatch = styleProperties.match(/fill:([^,}]+)/);
    if (!fillMatch) {
      // No fill color specified, don't modify
      return match;
    }

    const fillColor = fillMatch[1].trim();
    const normalizedFill = normalizeHexColor(fillColor);
    const textColor = getContrastTextColor(normalizedFill);

    // Add color attribute to the style declaration
    // Style properties are comma-separated, so we add color at the end
    const trimmedProperties = styleProperties.trim();
    return `style ${nodeId} ${trimmedProperties},color:${textColor}`;
  });
}

mermaid.initialize({
  startOnLoad: true,
  theme: 'base',
  themeVariables: {
    // Background & Canvas
    background: theme.backgroundLevel1,
    canvasBackground: theme.backgroundLevel1,

    // Primary Node Styles
    primaryColor: theme.chatCardBlue || theme.highlight,
    primaryTextColor: theme.textPrimary,
    primaryBorderColor: theme.selectedActive,

    // Secondary/Alternative Nodes
    secondaryColor: theme.backgroundLevel2,
    secondaryTextColor: theme.textSecondary,

    // Lines & Arrows
    lineColor: theme.infoPrimary,
    textColor: theme.textPrimary,
    mainBkg: theme.chatCardGray || theme.backgroundLevel3,
    nodeBorder: theme.border,

    // Specific components
    edgeLabelBackground: theme.backgroundLevel2,
    tertiaryColor: theme.backgroundLevel4,

    // Sequence Diagrams
    actorBkg: theme.highlight,
    actorBorder: theme.selectedActive,
    actorTextColor: theme.textPrimary,
    signalColor: theme.textLink,
    signalTextColor: theme.textSecondary,
    labelBoxBorderColor: theme.border,
    labelBoxBkgColor: theme.backgroundLevel3,

    // State/Class diagrams
    classText: theme.textPrimary,
    fillType0: theme.backgroundLevel2,

    // Error Handling (to keep it clean)
    errorBkgColor: theme.buttonDelete,
    errorTextColor: theme.statusCritical,
  },

  securityLevel: 'strict',
  suppressErrorRendering: true,
});

export const Mermaid: React.FC<{ code: string }> = ({ code }) => {
  const id = useId();
  const { data, status, error } = useQuery({
    queryKey: ['mermaid', code],
    queryFn: () => {
      // Preprocess code to add text colors for readability before rendering
      const enhancedCode = enhanceMermaidCodeForReadability(code);
      return mermaid.render(id, enhancedCode);
    },
  });

  if (status === 'pending') {
    return <ThinkingLoader params={{ message: 'Rendering chart...' }} />;
  }
  if (status === 'error' || !data) {
    return <GuardianErrorComponent error={error} />;
  }

  return (
    <div
      style={{ display: 'flex', justifyContent: 'center', padding: '1rem 0' }}
      className="mermaid"
      ref={(el) => {
        if (el && data.bindFunctions) {
          data.bindFunctions(el);
        }
      }}
      dangerouslySetInnerHTML={{ __html: data.svg }}
    />
  );
};
